import { useEffect, useRef, useState } from 'react';
import { LoggedInUser } from '../../../common/types';
import { fetchUserIdToken, LoginFetch } from '../../../service/login-api';
import { logger } from '../../../common/logger';
import { loginFetchClient } from '../../../service/loginFetchClient';

export function useIdToken(user: LoggedInUser | null) {
  const [token, setToken] = useState<string>();
  const timeoutId = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!user || timeoutId.current) return;

    let waitTime = 0;

    try {
      waitTime = getTokenRefreshWaitTime(token, Date.now());
    } catch (e) {
      logger.warn({ error: e }, 'extracting user id-token refresh time failed');
      return;
    }

    timeoutId.current = setTimeout(() => {
      void refreshToken(loginFetchClient()).then(token => {
        if (token) {
          setToken(token);
          timeoutId.current = null;
        }
      });
    }, waitTime);

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    };
  }, [token, user]);
}

const refreshToken = (loginFetch: LoginFetch) =>
  fetchUserIdToken(loginFetch)
    .then(async response => {
      if (!response.ok)
        throw new Error(`request failed with http ${response.status}`);

      return response
        .json()
        .then(body => {
          if (!body || !body.token)
            throw new Error('token missing from response');

          const token: string = body.token;

          return token;
        })
        .catch(e => {
          throw new Error(`extracting token from body failed: ${e}`);
        });
    })
    .catch(e => logger.warn({ error: e }, 'fetching user id token failed'));

const getTokenTimestamps = (token: string) => {
  const base64Payload = token.split('.')[1];

  if (!base64Payload) return null;

  const { exp, iat } = JSON.parse(atob(base64Payload));

  return {
    timeExpires: exp * 1000,
    timeIssued: iat * 1000,
  };
};

function getTokenRefreshWaitTime(token: string | undefined, timeNow: number) {
  if (!token) return 0;

  const timestamps = getTokenTimestamps(token);

  if (!timestamps) throw new Error('token is missing timestamps');

  const validityDuration = timestamps.timeExpires - timestamps.timeIssued;
  const validityDurationHalf = validityDuration / 2;
  const timeRefresh = timestamps.timeIssued + validityDurationHalf;

  const waitTime = Math.max(0, timeRefresh - timeNow);

  if (waitTime === 0) {
    throw new Error('token is already expired');
  }

  return waitTime;
}
